import React, { useEffect, useState } from "react";
import { Modal, Box, Typography, Button, TextField, IconButton, CircularProgress, Stack } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from "@mui/icons-material/Add";
import { apiCallWrapper } from "../../../utils/commonUtils";
import apiClient from "../../../axiosClient";
import HuntGroup from "../interface/huntGroup";
import Agent from "../interface/agent";
import HuntGroupAgentMapping from "../interface/huntGroupAgentMapping";
import DropDownValue from "../../baseComponents/dropdownvalue";

const modalStyle = {
	position: 'absolute' as 'absolute',
	top: '50%',
	left: '50%',
	transform: 'translate(-50%, -50%)',
	width: 500,
	bgcolor: 'background.paper',
	boxShadow: 24,
	p: 4,
	borderRadius: 2,
};

interface ManageAgentLicensesModalProps {
	open: boolean,
	onClose: () => void,
	huntGroup: HuntGroup,
	availableAgents: Agent[],
	refreshAgents: () => void
}

const ManageHuntGroupAgentsModal: React.FC<ManageAgentLicensesModalProps> = ({ open, onClose, huntGroup, availableAgents, refreshAgents }) => {
	const [huntGroupAgentMappings, setHuntGroupAgentMappings] = useState<HuntGroupAgentMapping[]>([]);
	const [deletedMapping, setMappingToBeDeleted] = useState<string[]>([]);
	const [addedMapping, setMappingToBeAdded] = useState<string[]>([]);
	const [loading, setLoading] = useState(false);
	const [submitting, setSubmitting] = useState(false);

	// Fetch existing licenses
	useEffect(() => {
		if (open && huntGroup) {
			const fetchLicenses = async () => {
				setLoading(true);
				const response = await apiCallWrapper(() => apiClient.get(`/api/v1/tcn_config/hunt_group_agent_mappings?hunt_group_id=${huntGroup.hunt_group_id}&is_active=true`));
				if (response && (response.status === 200 || response.status === 201)) {
					setHuntGroupAgentMappings(response.data);
				}
				setLoading(false);
			};
			fetchLicenses();
		}
	}, [open, huntGroup]);

	const createNewMappingRow = () => {
		setMappingToBeAdded([...addedMapping, ""]);
	};

	const removeMapping = (index: number, mapping_id: string) => {
		const updated = [...huntGroupAgentMappings];
		updated.splice(index, 1);
		setHuntGroupAgentMappings(updated);

		setMappingToBeDeleted([...deletedMapping, mapping_id]);
	};

	const addMapping = (index: number, value: string) => {
		const updated = [...addedMapping];
		updated[index] = value;
		setMappingToBeAdded(updated);
	};

	const removeNewlyAddedMapping = (index: number) => {
		const updated = [...addedMapping];
		updated.splice(index, 1);
		setMappingToBeAdded(updated);
	}

	const postDeletionRequest = async () => {
		const response = await apiCallWrapper(() => apiClient.post(`/api/v1/tcn_config/hunt_group_agent_mappings/bulk-delete`, deletedMapping));
		if (response.status === 200 || response.status === 201) {
			alert("Agent mapping deleted successfully");
		}
	}

	const postCreationRequest = async () => {
		let mapping_request: any = [];

		addedMapping.forEach((mapping) => {
			mapping_request.push({
				hunt_group_id: huntGroup.hunt_group_id,
				tcn_agent_id: mapping
			});
		});
		const response = await apiCallWrapper(() => apiClient.post(`/api/v1/tcn_config/hunt_group_agent_mappings`, mapping_request));
		if (response.status === 200 || response.status === 201) {
			alert("Agent mapping added successfully");
		}
	}

	const saveLicenses = async () => {
		try {
			setSubmitting(true);
			if (addedMapping.length > 0) {
				await postCreationRequest();
			}
			if (deletedMapping.length > 0) {
				await postDeletionRequest();
			}
		} catch (e) {
			console.error("Could not able to save changes: ", e);
		} finally {
			setSubmitting(false);
			onClose();
			refreshAgents();
		}
	};

	return (
		<Modal open={open} onClose={onClose}>
			<Box sx={modalStyle}>
				<Box display="flex" justifyContent="space-between" mb={2}>
					<Typography variant="h6">Licenses: {huntGroup.hunt_group_name}</Typography>
					<IconButton onClick={onClose}><CloseIcon /></IconButton>
				</Box>

				{loading ? <CircularProgress /> : (
					<Stack spacing={2} sx={{ maxHeight: 400, overflowY: 'auto', mb: 3, pr: 1 }}>
						{huntGroupAgentMappings.map((license, index) => (
							<Box key={license.id} sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 1, border: '1px solid #eee', borderRadius: 1 }}>
								<Typography sx={{ flexGrow: 1, fontWeight: 500 }}>{license.agent_username}</Typography>
								<Typography sx={{ flexGrow: 1, fontWeight: 500 }}>{license.tcn_agent_id}</Typography>
								<IconButton color="error" onClick={() => removeMapping(index, license.id)}>
									<DeleteIcon />
								</IconButton>
							</Box>
						))}
						{addedMapping.map((mapping, index) => (
							<Box key={mapping} sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 1, border: '1px solid #eee', borderRadius: 1 }}>
								<DropDownValue 
									label="Select Agent" 
									items={availableAgents} 
									selectedValue={mapping} 
									onSelection={(e: string) => addMapping(index, e)}
									keyProp="agent_id"
									valueProp="agent_id"
									displayProp="agent_username"
								/>
								<IconButton color="error" onClick={() => removeNewlyAddedMapping(index)}>
									<DeleteIcon />
								</IconButton>
							</Box>
						))}
					</Stack>
				)}

				<Button startIcon={<AddIcon />} variant="outlined" onClick={createNewMappingRow} fullWidth sx={{ mb: 3 }}>
					Add Agent License
				</Button>

				<Box display="flex" justifyContent="flex-end" gap={2}>
					<Button onClick={onClose}>Cancel</Button>
					<Button variant="contained" onClick={saveLicenses} disabled={submitting}>
						{submitting ? "Saving..." : "Save Changes"}
					</Button>
				</Box>
			</Box>
		</Modal>
	);
};

export default ManageHuntGroupAgentsModal;